// src/auth/useLegacySsoFlow.ts
//
// Custom hook that owns the Legacy SSO (Office.auth.getAccessToken) retry flow.
// Extracted from AuthContext to keep that file under the project's 500-line
// limit and to isolate the Office.js-specific error-code handling from the
// React-state plumbing.
//
// The hook receives state setters and tier helpers as deps and exposes
// `getAccessToken` — AuthContext calls it from `runInitialAuth` / cascade
// fallback / refresh. In-flight dedup lives here (`authPromiseRef`) so
// concurrent callers converge on a single Office API call.

import { useCallback, useRef, type MutableRefObject } from "react";
import { jwtDecode } from "jwt-decode";
import { track } from "../utils/analytics";
import { createLogger } from "../utils/logger";
import type { AuthTier } from "../config/authConfig";
import { LEGACY_SSO_TIMEOUT_MS } from "../config/authConfig";
import { mapOfficeSsoError } from "./officeSsoErrorMapper";
import {
  asOfficeAuthError,
  computeBackoffDelay,
  deriveUserFacingLegacyError,
  extractUserInfoFromToken,
  identifyAndEmitAppOpened,
  type UserInfo,
} from "../contexts/authHelpers";

const log = createLogger("LegacySsoFlow");

const AUTH_RETRY_ATTEMPTS = 3;
const AUTH_RETRY_DELAY = 2000;

export interface LegacySsoFlowDeps {
  setError: (e: string | null) => void;
  setAccessToken: (t: string | null) => void;
  setAuthCompleted: (b: boolean) => void;
  setUserInfo: (u: UserInfo | null) => void;
  setAllowFallback: (b: boolean) => void;
  setAuthTierBoth: (t: AuthTier) => void;
  detectAccountSwitch: (email: string) => boolean;
  timeoutsRef: MutableRefObject<Set<NodeJS.Timeout>>;
}

const isOfficeOnline = () =>
  Office.context.host === Office.HostType.Word &&
  Office.context.platform === Office.PlatformType.OfficeOnline;

export function useLegacySsoFlow(deps: LegacySsoFlowDeps) {
  const {
    setError,
    setAccessToken,
    setAuthCompleted,
    setUserInfo,
    setAllowFallback,
    setAuthTierBoth,
    detectAccountSwitch,
    timeoutsRef,
  } = deps;

  const authPromiseRef = useRef<Promise<string | null> | null>(null);

  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        const id = setTimeout(() => {
          timeoutsRef.current.delete(id);
          resolve();
        }, ms);
        timeoutsRef.current.add(id);
      }),
    [timeoutsRef]
  );

  const getAccessTokenFromOffice = useCallback(
    async (allowPrompt: boolean = false): Promise<string> => {
      try {
        return await Promise.race([
          Office.auth.getAccessToken({
            allowSignInPrompt: allowPrompt,
            allowConsentPrompt: true,
          }),
          new Promise<never>((_, reject) => {
            const id = setTimeout(() => {
              timeoutsRef.current.delete(id);
              reject(new Error("Authentication timeout"));
            }, LEGACY_SSO_TIMEOUT_MS);
            timeoutsRef.current.add(id);
          }),
        ]);
      } catch (error: unknown) {
        const officeErr = asOfficeAuthError(error);
        if (!officeErr.code) throw error;

        const mapped = mapOfficeSsoError(officeErr.code, allowPrompt);
        switch (mapped.kind) {
          case "retry":
            log.info("User not signed in, retrying with sign-in prompt");
            return getAccessTokenFromOffice(true);
          case "error":
            throw mapped.error;
          case "unknown":
            log.warn("SSO not supported or unknown error", { code: officeErr.code });
            throw error;
        }
      }
    },
    [timeoutsRef]
  );

  const getAccessToken = useCallback(
    async (attempt: number = 1): Promise<string | null> => {
      if (attempt === 1 && authPromiseRef.current) {
        log.info("Auth already in progress, returning existing promise");
        return authPromiseRef.current;
      }

      const performAuth = async (): Promise<string | null> => {
        try {
          setError(null);
          log.info(`Authentication attempt ${attempt}/${AUTH_RETRY_ATTEMPTS}`, {
            isWeb: isOfficeOnline(),
            hostType: Office.context.host,
            platform: Office.context.platform,
          });

          const token = await getAccessTokenFromOffice(false);
          const { name, email } = extractUserInfoFromToken(token);
          detectAccountSwitch(email);
          setAccessToken(token);
          setAuthCompleted(true);
          setUserInfo({ name, email });

          const decoded = jwtDecode<{ exp?: number }>(token);
          log.info("Legacy SSO token retrieved successfully", {
            hasEmail: Boolean(email),
            expiresIn: decoded.exp ? new Date(decoded.exp * 1000).toISOString() : "N/A",
            attempt,
          });
          identifyAndEmitAppOpened({ name, email }, "legacy_sso", decoded.exp, attempt);
          return token;
        } catch (error: unknown) {
          return await handleAttemptError(error, attempt);
        }
      };

      const handleAttemptError = async (
        error: unknown,
        attemptNum: number
      ): Promise<string | null> => {
        const officeErr = asOfficeAuthError(error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        const isThrottled =
          officeErr.code === 13013 || errorMessage.includes("Too many authentication attempts");

        log.warn(`Authentication attempt ${attemptNum} failed`, {
          error: errorMessage,
          errorCode: officeErr.code,
          willRetry: attemptNum < AUTH_RETRY_ATTEMPTS,
          isThrottled,
        });

        if (attemptNum < AUTH_RETRY_ATTEMPTS) {
          const backoff = computeBackoffDelay(attemptNum, AUTH_RETRY_DELAY, isThrottled);
          log.info(`Retrying after ${backoff}ms delay`, {
            retryType: isThrottled ? "exponential" : "linear",
            nextAttempt: attemptNum + 1,
          });
          await sleep(backoff);
          return await getAccessToken(attemptNum + 1);
        }

        log.error("Authentication failed after all retries, enabling fallback mode", {
          error: errorMessage,
          errorCode: officeErr.code,
          totalAttempts: AUTH_RETRY_ATTEMPTS,
          isWeb: isOfficeOnline(),
        });
        setAuthTierBoth("none");
        track("auth:degraded", {
          error_message: errorMessage,
          error_code: officeErr.code,
          total_attempts: AUTH_RETRY_ATTEMPTS,
          auth_tier: "none",
        });
        // Clear any previously-held token. On INITIAL auth this is a no-op
        // (accessToken was never set), but on the REFRESH path a stale token
        // would otherwise remain, keeping `canAccessBackend` true while every
        // backend call 401s against the expired token — exactly the loop the
        // circuit breaker has to catch. Nulling here keeps the invariant
        // `allowFallback=true ⟹ accessToken===null` intact.
        setAccessToken(null);
        setError(deriveUserFacingLegacyError(errorMessage));
        setAuthCompleted(true);
        setAllowFallback(true);
        return null;
      };

      if (attempt === 1) {
        authPromiseRef.current = performAuth();
        try {
          return await authPromiseRef.current;
        } finally {
          authPromiseRef.current = null;
        }
      }
      return performAuth();
    },
    [
      getAccessTokenFromOffice,
      detectAccountSwitch,
      sleep,
      setError,
      setAccessToken,
      setAuthCompleted,
      setUserInfo,
      setAllowFallback,
      setAuthTierBoth,
    ]
  );

  return { getAccessToken };
}
